import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { Card } from 'src/domains/players/webview/components/Account/common/card/Card';
import { BadgeGroup } from 'src/domains/players/webview/components/Account/common/badge/BadgeGroup';
import {
    ButtonsWrapper,
    CreditsCardButton,
    CreditsCardConditionsContainer,
    CreditsCardContainer,
    CreditsCardHeader,
    CreditsCardProgressBar,
    CreditsCardRow,
    CreditsCardRowLabel,
    CreditsCardRowValue,
} from './Credits.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Badge } from 'src/domains/players/webview/components/Account/common/badge/Badge';
import { BonusWinningsWalletViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { CreditsState } from './Credits.state';
import { Common, useCommon } from 'src/domains/common/Common';
import { TermsPage } from 'src/domains/players/webview/components/SignUp';
import { TermsDrawerState } from './TermsDrawer/TermsDrawer.state';
import {
    ApiGetBonusBalanceProgressResponse200Type,
    ApiGetBonusBalanceProgressResponseType,
} from 'src/api_openapi/v2/bonus-engine/apiGetBonusBalanceProgress';
import { ResourceCustomer, UsersState } from 'src/domains/players/state/UsersState';
import { computed } from 'mobx';
import { Amount } from 'src_common/common/amount/Amount';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';

const progressBarPercentage = (currentProgress: number, totalRequiredPlaythrough: number): number => {
    if (currentProgress === 0) {
        return 0;
    } else if (currentProgress >= totalRequiredPlaythrough) {
        return 100;
    }
    return (currentProgress / totalRequiredPlaythrough) * 100;
};

class CreditsBonusBalanceCardState {
    public readonly creditsBonusBalanceProgress: ResourceCustomer<ApiGetBonusBalanceProgressResponseType>;

    public constructor(
        private readonly common: Common,
        bonusId: string
    ) {
        this.creditsBonusBalanceProgress = new ResourceCustomer(
            'bonusBalance',
            common.session,
            async (): Promise<ApiGetBonusBalanceProgressResponseType> => {
                return await this.common.trpcClient.client.bonusEngineRouter.getBonusBalanceProgress.mutate({
                    bonusId,
                });
            }
        );
    }

    @computed public get progressData(): ApiGetBonusBalanceProgressResponse200Type | null {
        const progress = this.creditsBonusBalanceProgress.valueReady;

        if (progress === null) {
            return null;
        }

        if (progress.status === 200) {
            return progress.body;
        }

        return null;
    }
}

interface CreditsBonusBalanceCardPropsType {
    bonus: BonusWinningsWalletViewType;
    creditsState: CreditsState;
}

export const CreditsBonusBalanceCard = observer(
    'CreditsBonusBalanceCard',
    ({ bonus, creditsState }: CreditsBonusBalanceCardPropsType) => {
        const common = useCommon();
        const { customKeyboard } = AppPlayersState.get(common);
        const configState = ConfigComponents.get(common).config;
        const usersState = UsersState.get(common);

        const [state] = useState<TermsDrawerState>(() => new TermsDrawerState(common, customKeyboard, usersState));
        const [creditsBonusBalanceCardState] = useState<CreditsBonusBalanceCardState>(
            () => new CreditsBonusBalanceCardState(common, bonus.bonusId)
        );

        const campaignId = bonus.campaignId ?? null;
        const progressData = creditsBonusBalanceCardState.progressData;

        return (
            <Card haveBorder={true}>
                {state.displayTermsPage ? (
                    <TermsPage
                        title='TERMS & CONDITIONS'
                        content={state.terms ?? 'Error occurred'}
                        closePage={state.closePage}
                        hasArrow
                    />
                ) : null}
                <CreditsCardContainer>
                    <div>
                        <CreditsCardHeader variant={configState.componentVariants.accountSectionVariant}>
                            <div>{creditsState.getCreditValue(bonus.balance)}</div>
                            <div>
                                <Badge type={creditsState.mapBadgeTypeBasedOnStatus(bonus.status)}>
                                    {bonus.status}
                                </Badge>
                            </div>
                        </CreditsCardHeader>
                    </div>
                    <CreditsCardConditionsContainer>
                        {bonus.eligibleGames === undefined || bonus.eligibleGames === null ? null : (
                            <CreditsCardRow>
                                <CreditsCardRowLabel>
                                    <I18n
                                        langKey='account.credits.eligibleGames:'
                                        defaultText='Eligible Games:'
                                    />
                                </CreditsCardRowLabel>
                                <CreditsCardRowValue>
                                    <BadgeGroup badges={creditsState.mapEligibleGamesToNames(bonus.eligibleGames)} />
                                </CreditsCardRowValue>
                            </CreditsCardRow>
                        )}

                        {bonus.playthrough === undefined || bonus.playthrough === null ? null : (
                            <CreditsCardRow>
                                <CreditsCardRowLabel>
                                    <I18n
                                        langKey='account.credits.playthrough'
                                        defaultText='Play Through'
                                    />
                                </CreditsCardRowLabel>
                                <CreditsCardRowValue>{bonus.playthrough}x</CreditsCardRowValue>
                            </CreditsCardRow>
                        )}

                        {progressData === null ? null : (
                            <CreditsCardRow>
                                <CreditsCardRowLabel>
                                    <I18n
                                        langKey='account.credits.progress'
                                        defaultText='Progress'
                                    />
                                </CreditsCardRowLabel>

                                <CreditsCardProgressBar
                                    currentProgress={progressBarPercentage(
                                        progressData.currentProgress,
                                        progressData.totalRequiredPlaythrough
                                    )}
                                />

                                <div>
                                    {new Amount(progressData.currentProgress.toString()).format('GBP')} of{' '}
                                    {new Amount(progressData.totalRequiredPlaythrough.toString()).format('GBP')}
                                </div>
                            </CreditsCardRow>
                        )}
                    </CreditsCardConditionsContainer>
                    <ButtonsWrapper>
                        {campaignId === null ? null : (
                            <CreditsCardButton
                                size='small'
                                isLeft
                                onClick={(): void => state.showTermsPage('free-spins', 'campaign', bonus.campaignId)}
                                version='tertiary-dark'
                            >
                                <I18n
                                    langKey='account.credits.view-terms'
                                    defaultText='View Terms'
                                />
                            </CreditsCardButton>
                        )}

                        <CreditsCardButton
                            size='medium'
                            onClick={(): void => creditsState.redirectToCasino(null)}
                            disabled={creditsState.isButtonDisabled(bonus.status)}
                        >
                            <I18n
                                langKey='account.credits.play-now'
                                defaultText='Play Now'
                            />
                        </CreditsCardButton>
                    </ButtonsWrapper>
                </CreditsCardContainer>
            </Card>
        );
    }
);
